import React, { FC, useEffect, useCallback } from 'react';
import {
  useAdyen,
  useBridgeApi,
  useDeviceInfo,
  useLogger,
  useSelector,
  useTextToImage,
  useDispatch,
  useI18n,
} from '~Hooks';
import { useStorage } from '~Components/Storage';
import * as actions from '~actions';
import { combineLogoAndText } from './utils';
import { renderAdyenPrintJob, renderReceiptDocument } from './AdyenReceipt';
import { ReceiptDocument } from '~Hooks/Entities/useReceipt/document';

// Models that comes from the StarPrnt library in order-manager-app
export const OLD_STAR_PRINTER_MODELS = ['MCP31L', 'MCP31LB', 'MCP21LB', 'StarPrint'];

export const PRINTERS_WITHOUT_STATUS = [
  ...OLD_STAR_PRINTER_MODELS,
  'StarCloudPrinter',
  'VIRTUAL_TEST',
];

export const MODEL_WIDTHS: { [key: string]: number } = {
  EpsonSocket: 24, // This is the model name for epson in order-manager-app in DEV and not a real model name

  'TM-m30III-H': 24, // Epson TM-m30III-H
  'TM-m30III': 24, // Epson TM-m30III
  'TM-m30II': 23, // Epson TM-m30II

  'TM-T20III': 23, // Epson TM-T20III
  'TM-T20II': 23, // Epson TM-T20II
  'TM-M30': 23, // Epson TM-M30
  'TM-m30': 23, // Epson TM-m30
  'TM-T88VI': 21, // Epson TM-T88VI
  'TM-T20IV': 24, // Epson TM-T20IV

  mPOP: 16, // Star mPOP
  mC_Print2: 16, // Star mC_Print2
  // mC_Print3: 24, This gets applied dynamically if we detect that we're on Android, otherwise on iOS it makes it overflow
  'mC-Print3': 24, // Star mC-Print3

  StarCloudPrinter: 24, // Star mC-Print3 however this might differ since there can be other star printers that can be used as cloud printer
};

export const DEFAULT_WIDTH = 17;

// We use printType mainly for logging purposes to know what kind of print we are doing
// Print type RECEIPT is the one with some special handling
export type PrintType =
  | 'BONG' // Bong is a kitchen tickets
  | 'RECEIPT' // Receipts and receipt copies
  | 'COMPETITION_SCORE' // Printing score for all competitiors in a competition
  | 'TEST_PRINT' // Test print, used for testing the printer
  | 'Z_REPORT' // Z-report
  | 'EPOS_ORDER_NUMBER' // Order number for EPOS, the number the customer gets
  | 'REPORT' // Printing the report from the report page
  | 'START_DAY' // Printing the start day and
  | 'INVENTORY' // Printing the inventory list
  | 'GIFT_CARD' // Printing the gift card with QR code
  | 'SWISH' // Prints the swish QR code for doing a payment
  | 'KLARNA' // Prints the Adyen pay-by-link QR code the shopper pays Klarna with
  | 'PAYMENT_SCAN' // Payment scan
  | 'CASH_CHANGE'; // Printing withdrawals and deposits of cash

/* Important note: We send in the alignment field as lowercase since that's the terminology in order-manager-app */
export type Alignment = 'LEFT' | 'CENTER' | 'RIGHT';

/* Important note: For PrintType "RECEIPT" - Receipts only uses content & fontSize. The alignment is instead done by applying space to the left/right. */
export type PrintRow = {
  content: string;
  fontSize?: number;
  bold?: boolean;
  condensed?: boolean;
  alignment?: Alignment;
  doubleHeigth?: boolean;
  doubleWidth?: boolean;

  qr?: boolean /* If this is true - the content string will transformed into QR code in order-manager-app */;
};

type PrintPayload = {
  data?: string | PrintRow[]; // string: We just print the string directly (e.g. for z-reports) or PrintRow: We print with formatting (e.g. for bongs)
  printerId?: string;
  orderId?: string;
  printType: PrintType; // Required to make loggers more informative
  base64Image?: string; // Used for printing images (e.g. from bong master)
  /**
   * Structured receipt, sent alongside `data` for RECEIPT prints. Printers that render
   * images (Adyen terminals) lay this out with real typography instead of the text rows.
   */
  receipt?: ReceiptDocument;
};

export interface PrinterManager {
  initiatePrint: (obj: PrintPayload) => void;
}

// Extend window object with printerManager
declare global {
  interface Window {
    printerManager?: PrinterManager;
  }
}

/* Handles print jobs for the app, selecting the appropriate source (e.g., Cloud Printer or Adyen Terminal). */
const PrinterManager: FC = () => {
  const logger = useLogger('printer-manager');
  const adyen = useAdyen();
  const venueId = useSelector((state) => state.venueId);
  const deviceInfo = useDeviceInfo();
  const dispatch = useDispatch();
  const POSPrint = useSelector((state) => state.POSReceiptPrints);
  const printers = useSelector((state) => state.printers);
  const primaryPrinter = printers.find((printer) => printer.primary);

  const bridgeApi = useBridgeApi();
  const [storageData] = useStorage();
  const textToImage = useTextToImage();
  const { i18n } = useI18n();

  const printDone = (printType?: PrintType) => {
    if (printType === 'RECEIPT') {
      logger.info('Clearing receipt print state');
      dispatch({
        type: actions.CLEAR_RECEIPT,
      });
    }
  };

  const initiatePrint = useCallback(
    async (printerPayload: PrintPayload) => {
      const printerId = printerPayload.printerId || primaryPrinter?.id;
      const printerModel = printers.find((printer) => printer.id === printerId)?.model;
      logger.info('Initiate print. ID: ' + printerId, printerPayload);
      const foundPrinter = printers.find((printer) => printer.id === printerId);

      const isAdyenHandheldTerminal = deviceInfo?.isAdyenTerminal;

      /* Console logs for DEV testing purposes */
      if (typeof printerPayload.data === 'string') {
        console.log(printerPayload.data);
      } else if (printerPayload.data) {
        console.log(printerPayload.data.map((l) => l.content).join('\n'));
      }

      // If we should print from terminal and terminal has support for printing.
      const currentTerminal = isAdyenHandheldTerminal
        ? storageData.adyenTerminals[0] // We only have one terminal so we can just pick the first one
        : storageData.adyenTerminals.find((t) => t.POIID === POSPrint?.terminal); // This is when the device is and Ipad and has adyen terminals connected to it

      /* SOURCE: Adyen terminal - Print via Adyen terminal */
      if (
        (printerPayload.printType === 'RECEIPT' && currentTerminal?.hasPrinter) ||
        (!foundPrinter && currentTerminal?.hasPrinter)
      ) {
        const currentTerminalId = currentTerminal?.POIID;

        logger.info('Initiating print via Adyen terminal', {
          data: {
            currentTerminal,
            printType: printerPayload.printType,
            hasLogo: Boolean(printerPayload.base64Image),
            adyenTerminals: storageData.adyenTerminals,
          },
        });

        if (!currentTerminalId) {
          logger.warn('Failed to initiate print, no terminal found', {
            currentTerminalId,
          });
          return;
        }

        // The terminal's text print mode gives us no control over fonts or alignment, so
        // every print is rasterised into 384px wide monochrome images instead. Receipts are
        // laid out from their structured document; other prints (bongs, reports) from their
        // rows. A QR code, if any, is printed afterwards with the terminal's barcode printer.
        const foundQR =
          !printerPayload.data || typeof printerPayload.data === 'string'
            ? undefined
            : printerPayload.data.find((row) => row.qr);

        let images: string[] = [];

        try {
          images = printerPayload.receipt
            ? await renderReceiptDocument(printerPayload.receipt, printerPayload.base64Image)
            : await renderAdyenPrintJob({
                data: printerPayload.data,
                logoDataUrl: printerPayload.base64Image,
              });
        } catch (error) {
          logger.error('Failed to render print for Adyen terminal', {
            printType: printerPayload.printType,
            errorMessage: error instanceof Error ? error.message : String(error),
          });
          printDone(printerPayload.printType);
          return;
        }

        if (!images.length && !foundQR) {
          logger.warn('Nothing to print on Adyen terminal', {
            printType: printerPayload.printType,
          });
          printDone(printerPayload.printType);
          return;
        }

        logger.info('Sending rendered print to Adyen terminal', {
          printType: printerPayload.printType,
          segments: images.length,
          hasQR: Boolean(foundQR),
        });

        // The print job is fully rendered and handed to the terminal from here on, so release
        // the receipt print state now rather than after the (slow) terminal round trips.
        printDone(printerPayload.printType);

        const result = await adyen.printImages(currentTerminalId, images);

        if (!result.success) {
          logger.error('Adyen terminal print failed', {
            printType: printerPayload.printType,
            printedSegments: result.printed,
            totalSegments: images.length,
            failure: result.message,
          });
          dispatch(actions.addToast(i18n('Printer.Adyen.PrintFailed'), true));
        } else if (foundQR) {
          const qrResult = await adyen.printQR(currentTerminalId, foundQR.content);

          if (!qrResult.success) {
            logger.warn('Adyen terminal QR print failed', {
              printType: printerPayload.printType,
            });
          }
        }

        return;
      }

      // Check if the selected printer is the Zettle Dock
      const selectedPrinterIsZettleDock =
        foundPrinter?.id === 'DockingStation' || (!foundPrinter && deviceInfo?.isZettleTerminal);

      // Check if it is a BONG print and if we should print it on the Zettle Dock
      const shouldPrintBongOnZettleDock =
        printerPayload.printType === 'BONG' &&
        storageData.useHandheldBongPrinter &&
        selectedPrinterIsZettleDock;

      // Check if we should print on the Zettle Dock
      const shouldPrintOtherOnZettleDock =
        printerPayload.printType !== 'BONG' &&
        storageData.useHandheldPrinter &&
        selectedPrinterIsZettleDock;

      const shouldPrintOnZettleDock = shouldPrintBongOnZettleDock || shouldPrintOtherOnZettleDock;

      /* SOURCE: Zettle dock - Print via zettle dock */
      if (shouldPrintOnZettleDock) {
        logger.info('Initiating print via Zettle dock', printerPayload);

        logger.info('[DEBUG] zettle printing', {
          data: {
            printerPayload: printerPayload,
            foundPrinter: foundPrinter,
            deviceInfo: deviceInfo,
            useHandheldBongPrinter: storageData.useHandheldBongPrinter,
            useHandheldPrinter: storageData.useHandheldPrinter,
          },
        });

        // Convert text data to base64 image for Zettle dock
        // Zettle dock expects a base64 image, so plain strings (e.g. test prints) must be converted
        const textBase64 =
          typeof printerPayload.data === 'string'
            ? textToImage(
                printerPayload.data
                  .split('\n')
                  .map((line) => ({ content: line, alignment: 'CENTER' as Alignment })),
              )
            : printerPayload.data
            ? textToImage(printerPayload.data)
            : '';

        if (printerPayload.base64Image && textBase64) {
          try {
            const combined = await combineLogoAndText(
              printerPayload.base64Image,
              textBase64,
              logger,
            );
            bridgeApi.printZettleDock(combined);
          } catch (error) {
            logger.warn('Failed to combine logo with text for Zettle, printing text only', {
              errorData: error,
            });
            bridgeApi.printZettleDock(textBase64);
          }
        } else {
          bridgeApi.printZettleDock(textBase64);
        }

        printDone(printerPayload.printType);
        return;
      }

      if (!foundPrinter || !printerId) {
        logger.error(`Initiating print failed, printer ID ${printerId} not found`, printerPayload);
        return;
      }

      logger.info('Initiating print', printerPayload);

      // For receipts, we apply alignment by adding space to the left/right so we don't want to send alignment center to the printer since it will mess up the formatting
      // For zettle, adyen and cloud printers we need the center align to be sent to the printer that is why it is removed here
      const data =
        printerPayload.printType === 'RECEIPT' && typeof printerPayload.data !== 'string'
          ? printerPayload.data?.map((row) => ({
              ...row,
              alignment: 'LEFT' as Alignment,
            }))
          : printerPayload.data;

      /* Default print */
      dispatch(
        actions.addPrintToQueue({
          printer: printerId,
          printerModel: printerModel ?? 'Unknown model',
          data: data,
          orderId: printerPayload.orderId,
          printType: printerPayload.printType,
          base64Image: printerPayload.base64Image,
        }),
      );
      printDone(printerPayload.printType);
    },
    [
      venueId,
      primaryPrinter?.id,
      storageData.adyenTerminals,
      storageData.useHandheldPrinter,
      storageData.useHandheldBongPrinter,
      deviceInfo,
      POSPrint,
      printers,
      i18n,
    ],
  );

  /* Append our printerManager to window object */
  useEffect(() => {
    if (venueId) {
      window.printerManager = {
        initiatePrint: (obj) => initiatePrint(obj),
      };
    }

    return () => {
      delete window.printerManager;
    };
  }, [initiatePrint, venueId]);

  return <></>;
};

export default PrinterManager;
